import { useState } from "react";
import { batch } from "react-redux";

import { LanguagesAbbr, Session } from "../../../../../../../../types/working-model";
import { useAppDispatch, useTypedSelector } from "../../../../../../../../store/reducers/use-typed-selector";
import ModalComponent from "@general-ui/modal/modal";
import languageLabelMap from '../../../../../../../../utils/translate-langs-map.json';
import GradientButton from "@general-ui/button/gradient-button";
import WaitingIndicator from "@general-ui/waiting-indicator/waiting-indicator";
import { showAlertLong } from "@general-ui/alert/alert-service";
import { updateSession, updateWorkingSession } from "store/actions/admin/create-event/session";
import { sessionLanguagesUpdated, setEventLanguages, updateMultipleSessions } from "store/actions/admin/create-event";

import './remove-language-modal.scss';

interface IRemoveLanguageConfirmationModal {
	languageToRemove: LanguagesAbbr | null;
	handleClose: () => void;
}

function RemoveLanguageConfirmationModal({ languageToRemove, handleClose }: IRemoveLanguageConfirmationModal): JSX.Element {
	const dispatch = useAppDispatch();

	const token = useTypedSelector(state => state.AuthReducer.token);
	const workingEvent = useTypedSelector(state => state.CreateEventReducer.workingEvent);
	const workingSession = useTypedSelector(state => state.CreateSessionReducer.workingSession);
	const eventLanguages = useTypedSelector(state => state.CreateEventReducer.workingEvent?.settings.i18n?.event_languages);

	const [saving, setSaving] = useState(false);

	const handleSave = (sessionsToRemove: 'single' | 'all') => {
		if (!languageToRemove || !workingEvent || !workingSession || !token || !eventLanguages) {
			return;
		}

		try {
			setSaving(true);

			const updatedWorkingSession = {
				...workingSession,
				languages: workingSession.languages.filter(lang => lang !== languageToRemove)
			};

			batch(() => {
				if (sessionsToRemove === 'single') {
					dispatch(updateSession(updatedWorkingSession, token));
				}
				dispatch(updateWorkingSession(updatedWorkingSession));
				dispatch(sessionLanguagesUpdated(workingSession.session, updatedWorkingSession.languages as LanguagesAbbr[]));

				if (sessionsToRemove === 'all') {
					// Remove the language from every session
					const updatedWorkingEventSessions = workingEvent.sessions.reduce((acc: Session[], cur: Session) => {
					// Don't bother to update if that session doesn't have the language
					if (!cur.languages.includes(languageToRemove)) return acc;

					return [
							...acc,
							{
								...cur,
								languages: cur.languages.filter(lang => lang !== languageToRemove)
							}
						];
					}, []);

					dispatch(updateMultipleSessions(updatedWorkingEventSessions, token));
					dispatch(setEventLanguages(eventLanguages.filter(lang => lang !== languageToRemove)));
				}
			});
		} catch (e) {
			console.error(e);
			showAlertLong({
				message: 'Unable to remove language',
				description: 'Please try again, or contact support if this persists',
				type: 'error'
			});
		} finally {
			setSaving(false);
			handleClose();
		}
	};

	return (
		<ModalComponent
			/* @ts-expect-error json import */
			title={`Are you sure you want to remove ${languageLabelMap[languageToRemove]}?`}
			className="remove-language-modal"
			open={!!languageToRemove}
			padTitle={false}
			onRequestClose={handleClose}
			closeable
			cancellable
			footer={
				<>
					<button className="secondary" onClick={() => handleSave('single')}>
						Hide for this session
					</button>
					<GradientButton
						fullBackgroundGradient
						color="black"
						gradient="--gradient-sunrise"
						buttonProps={{
							onClick: () => handleSave('all'),
							disabled: saving
						}}
					>
						{saving ? <WaitingIndicator /> : 'Yes, remove from project'}
					</GradientButton>
				</>
			}
		>
			<div>Choose "Hide for this session" if you'd like to keep this language in other sessions.</div>
		</ModalComponent>
	);
}

export default RemoveLanguageConfirmationModal;